import React from "react";
import { useWeeklySchedule } from "./use-weekly-schedule";
import {
  ArrowBlack,
  BreadCrumb,
  Dates,
  JadwalSecondaryText,
  JadwalText,
  KelasLiveBreadCrumb,
  Minggu,
  PageLoaderContainer,
  ScrollContainer,
  WeekContainer,
  WeeklyScheduleBreadcrumb,
  WeeklyScheduleEmptyContainer,
  WeeklyScheduleEmptySubTitle,
  WeeklyScheduleEmptyTitle,
  WeeklyScheduleIconContainer,
  WeeklyScheduleOutlineButton,
  WeeklyScheduleSessions,
  WeeklyScheduleSessionTimline,
  WeeklyScheduleTimeline,
  WeeklyScheduleVerticalTimeline,
  WeeklyScheduleIconTimelineContainer,
  WeeklyScheduleVerticalLine,
  TooltipSpan
} from "./weekly-schedule.styles";

import Image from "next/legacy/image";
import WeeklyScheduleIcon from "public/assets/kelas-live/weekly_schedule_icon.svg";
import { addWeeks, endOfWeek, format, startOfWeek } from "date-fns";
import { ClassReportCard } from "../class-report-card/class-report-card";
import { UpcomingClassCard } from "../upcoming-class-card/upcoming-class-card";
import { isOngoingClass } from "../utils";
import { OngoingClassCard } from "../ongoing-class-card/ongoing-class-card";
import NaraDetective from "public/assets/kelas-live/nara-detective.svg";
import { LoadingSpinner } from "../../icons/loading-spinner";
import { PastClassDetailModal } from "../past-class-detail-modal/past-classes-detail-modal";
import { ClassDetailModal } from "../class-detail-modal/class-detail-modal";
import { ClassSessionType } from "../hooks/use-user-landing/use-user-landing.types";
import { ClassSession } from "../../../services/api/sessions/sessionService.types";
import { ClassJoinModal } from "../class-join-modal/class-join-modal";
import PlayColorIcon from "public/assets/kelas-live/play-color-icon.svg";
import PlayTransparentIcon from "public/assets/kelas-live/play-transparent-icon.svg";
import { sizes } from "../../../styles";
import { ScrollSnapSlider } from "components/web-checkout/scroll-snap-carousel/scroll-snap-carousel";
import JadwalChangesInfoFromFeature from "components/jadwal/jadwal-changes-info/jadwal-changes-info-from-feature";

const WeeklySchedule = (): JSX.Element => {
  const {
    subscribedCourses,
    handleChangeCourse,
    selectedCourse,
    noOfWeeks,
    slotStartDate,
    handleWeeklySessionClick,
    selectedWeekStartDate,
    selectedWeekEndDate,
    sessions,
    baseMediaUrl,
    primaryUser,
    subscriptionStatus,
    currentTimeStamp,
    selectedPastSession,
    setSelectedPastSession,
    modalClassSession,
    setModalClassSession,
    modalType,
    setModalType,
    showJoiningClassPopUp,
    getJoinLinkURL,
    scrollRefs
  } = useWeeklySchedule();

  if (
    !subscribedCourses ||
    !noOfWeeks ||
    !subscriptionStatus ||
    !scrollRefs.current
  ) {
    return (
      <PageLoaderContainer>
        <LoadingSpinner />
      </PageLoaderContainer>
    );
  }

  return (
    <div>
      <BreadCrumb>
        <KelasLiveBreadCrumb>Kelas Live</KelasLiveBreadCrumb>
        <ArrowBlack> &gt; </ArrowBlack>
        <WeeklyScheduleBreadcrumb>Jadwal mingguan</WeeklyScheduleBreadcrumb>
      </BreadCrumb>

      <ScrollSnapSlider width={sizes.scrollWeeklyScheduleWidth}>
        {subscribedCourses.map((course) => {
          return (
            <WeeklyScheduleOutlineButton
              variant="secondary"
              onClick={() => handleChangeCourse(course)}
              isSelected={course.slotId === selectedCourse?.slotId}
              key={course.courseId}
            >
              {course.course.name}
            </WeeklyScheduleOutlineButton>
          );
        })}
      </ScrollSnapSlider>

      <WeeklyScheduleIconContainer>
        <Image
          src={WeeklyScheduleIcon}
          height={100}
          width={100}
          alt="Weekly schedule"
        />

        <div>
          <JadwalText>Jadwal mingguan</JadwalText>
          <JadwalSecondaryText>
            Cek jadwal lengkap tiap minggu di sini.
          </JadwalSecondaryText>
        </div>
      </WeeklyScheduleIconContainer>

      <JadwalChangesInfoFromFeature />

      <ScrollContainer>
        <ScrollSnapSlider
          width={sizes.scrollWeeklyScheduleWidth}
          height={sizes.scrollWeeklyScheduleHeight}
        >
          {noOfWeeks &&
            [...Array(noOfWeeks)].map((_, i) => {
              const weekStartDate = addWeeks(
                startOfWeek(new Date(slotStartDate), { weekStartsOn: 1 }),
                i
              ).toISOString();
              const weekEndDate = addWeeks(
                endOfWeek(new Date(slotStartDate), { weekStartsOn: 1 }),
                i
              ).toISOString();

              return (
                <WeekContainer
                  ref={scrollRefs.current[i]}
                  onClick={() =>
                    handleWeeklySessionClick(weekStartDate, weekEndDate, i)
                  }
                  isSelected={
                    selectedWeekStartDate === weekStartDate &&
                    selectedWeekEndDate === weekEndDate
                  }
                  key={i}
                >
                  {weekStartDate ===
                    startOfWeek(new Date(), {
                      weekStartsOn: 1
                    }).toISOString() &&
                    weekEndDate ===
                      endOfWeek(new Date(), {
                        weekStartsOn: 1
                      }).toISOString() && <TooltipSpan>Minggu ini</TooltipSpan>}
                  <Minggu>Minggu {i + 1}</Minggu>

                  <Dates>
                    {format(new Date(weekStartDate), "dd MMM")} -{" "}
                    {format(new Date(weekEndDate), "dd MMM")}
                  </Dates>
                </WeekContainer>
              );
            })}
        </ScrollSnapSlider>
      </ScrollContainer>

      <WeeklyScheduleSessions>
        {sessions && sessions.length > 0 ? (
          <WeeklyScheduleTimeline>
            <WeeklyScheduleVerticalTimeline>
              {sessions.map((session: ClassSession, index) => {
                return (
                  <>
                    <WeeklyScheduleIconTimelineContainer key={session.id}>
                      {session.endDate > currentTimeStamp ? (
                        <Image
                          src={PlayTransparentIcon}
                          height={30}
                          width={30}
                          alt="play icon"
                        />
                      ) : (
                        <Image
                          src={PlayColorIcon}
                          height={30}
                          width={30}
                          alt="Play icon"
                        />
                      )}
                    </WeeklyScheduleIconTimelineContainer>
                    {index + 1 !== sessions.length && (
                      <WeeklyScheduleVerticalLine />
                    )}
                  </>
                );
              })}
            </WeeklyScheduleVerticalTimeline>
            <WeeklyScheduleSessionTimline>
              {sessions.map((session: ClassSession) => {
                if (session.endDate < currentTimeStamp) {
                  return (
                    <ClassReportCard
                      key={session.id}
                      classSession={session}
                      handleShowMoreDetails={() => {
                        setSelectedPastSession(session);
                      }}
                      isWidthApplied={false}
                    />
                  );
                } else if (
                  isOngoingClass(
                    session.startDate,
                    session.endDate,
                    currentTimeStamp
                  )
                ) {
                  return (
                    <OngoingClassCard
                      key={session.id}
                      classSession={session}
                      subscriptionStatus={subscriptionStatus}
                      baseMediaURL={baseMediaUrl}
                      currentTimeStamp={currentTimeStamp}
                      primaryUser={primaryUser}
                      handleShowMoreDetails={() => {
                        setModalClassSession(session);
                        setModalType(ClassSessionType.ONGOING);
                      }}
                      handleJoinClass={() => getJoinLinkURL(session.id)}
                    />
                  );
                } else if (session.endDate > currentTimeStamp) {
                  return (
                    <UpcomingClassCard
                      key={session.id}
                      isWidthApplied={false}
                      classSession={session}
                      baseMediaURL={baseMediaUrl}
                      currentTimeStamp={currentTimeStamp}
                      handleShowMoreDetails={() => {
                        setModalClassSession(session);
                        setModalType(ClassSessionType.UPCOMING);
                      }}
                    />
                  );
                }
              })}
            </WeeklyScheduleSessionTimline>
          </WeeklyScheduleTimeline>
        ) : (
          <WeeklyScheduleEmptyContainer>
            <Image
              src={NaraDetective}
              height={300}
              width={300}
              alt="Nara as a detective"
            />
            <WeeklyScheduleEmptyTitle>
              Hmm, nggak ada jadwal nih
            </WeeklyScheduleEmptyTitle>
            <WeeklyScheduleEmptySubTitle>
              Coba pilih pelajaran lain atau minggu lainnya, ya.
            </WeeklyScheduleEmptySubTitle>
          </WeeklyScheduleEmptyContainer>
        )}
      </WeeklyScheduleSessions>

      {selectedPastSession &&
        baseMediaUrl &&
        currentTimeStamp &&
        subscriptionStatus && (
          <PastClassDetailModal
            classSession={selectedPastSession}
            baseMediaURL={baseMediaUrl}
            handleClose={() => setSelectedPastSession(null)}
            subscriptionType={subscriptionStatus.subscriptionType}
            primaryUser={primaryUser}
            subscriptionStatus={subscriptionStatus}
          />
        )}

      {baseMediaUrl && currentTimeStamp && modalClassSession && modalType && (
        <ClassDetailModal
          subscriptionStatus={subscriptionStatus}
          classSession={modalClassSession}
          baseMediaURL={baseMediaUrl}
          currentTimeStamp={currentTimeStamp}
          classType={modalType}
          handleClose={() => {
            setModalClassSession(null);
            setModalType(null);
          }}
          handleJoinClass={() => getJoinLinkURL(modalClassSession.id)}
          primaryUser={primaryUser}
        />
      )}

      {showJoiningClassPopUp && (
        <ClassJoinModal
          primaryUser={primaryUser}
          subscriptionType={subscriptionStatus.subscriptionType}
        />
      )}
    </div>
  );
};

export { WeeklySchedule };
